<template>
  <div class="data-form">
    <form @submit.prevent="submitForm" class="measurement-form">
      <!-- Date et Heure -->
      <div class="form-row">
        <div class="form-group">
          <label for="date">Date :</label>
          <input 
            id="date"
            v-model="formData.date" 
            type="date" 
            required 
            class="form-input"
          />
        </div>
        <div class="form-group">
          <label for="time">Heure :</label>
          <input 
            id="time"
            v-model="formData.time" 
            type="time" 
            required 
            class="form-input"
          />
        </div>
      </div>

      <!-- Indicateurs corporels -->
      <div class="metrics-grid">
        <div 
          v-for="metric in metrics" 
          :key="metric.key" 
          class="metric-group"
        >
          <label :for="metric.key">{{ metric.label }} :</label>
          <div class="input-row">
            <div class="input-group">
              <input 
                :id="metric.key"
                v-model.number="formData.data[metric.key].value" 
                :type="metric.type || 'number'"
                :step="metric.step || '0.1'"
                :min="metric.min || '0'"
                :max="metric.max"
                :required="!metric.optional" 
                class="form-input"
                :class="getInputClass(metric.key)"
                @input="validateMetric(metric.key)"
              />
              <span class="unit">{{ metric.unit }}</span>
            </div>
            <div class="status-group">
              <label :for="`${metric.key}-status`" class="status-label">Statut :</label>
              <select 
                :id="`${metric.key}-status`"
                v-model="formData.data[metric.key].status"
                class="status-select"
                :class="getStatusSelectClass(metric.key)"
              >
                <option value="faible">🔵 Faible</option>
                <option value="standard">🟢 Standard</option>
                <option value="excellent">🟢 Excellent</option>
                <option value="eleve">🟡 Élevé</option>
                <option value="trop-eleve">🔴 Trop élevé</option>
              </select>
            </div>
          </div>
          <div v-if="validationErrors[metric.key]" class="error-message">
            {{ validationErrors[metric.key] }}
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="form-actions">
        <button type="button" @click="resetForm" class="btn-reset">
          Réinitialiser
        </button>
        <button type="submit" :disabled="!isFormValid" class="btn-submit">
          Enregistrer la Mesure
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'

export default {
  name: 'DataForm',
  props: {
    currentUser: {
      type: String,
      required: true
    }
  },
  emits: ['measurement-added'],
  setup(props, { emit }) {
    // Définition des métriques avec leurs plages de valeurs normales
    const metrics = ref([
      { key: 'poids', label: 'Poids', unit: 'kg', min: 30, max: 200, step: '0.001' },
      { key: 'imc', label: 'IMC', unit: '', min: 10, max: 50, step: '0.1' },
      { key: 'graisse_corporelle', label: 'Graisse Corporelle', unit: '%', min: 0, max: 50, step: '0.1' },
      { key: 'frequence_cardiaque', label: 'Fréquence Cardiaque', unit: 'BPM', min: 30, max: 200, type: 'number', step: '1', optional: true },
      { key: 'indice_cardiaque', label: 'Indice Cardiaque', unit: '', min: 0, max: 20, step: '0.1', optional: true },
      { key: 'taux_musculaire', label: 'Taux Musculaire', unit: '%', step: '0.1' },
      { key: 'poids_sans_graisse', label: 'Poids sans Graisse', unit: 'kg', min: 20, max: 150, step: '0.1' },
      { key: 'graisse_sous_cutanee', label: 'Graisse Sous-cutanée', unit: '%', min: 0, max: 40, step: '0.1' },
      { key: 'graisse_viscerale', label: 'Graisse Viscérale', unit: '', min: 0, max: 30, type: 'number', step: '0.1' },
      { key: 'eau_corporelle', label: 'Eau Corporelle', unit: '%', min: 30, max: 80, step: '0.1' },
      { key: 'muscle_squelettique', label: 'Muscle Squelettique', unit: '%', min: 0, max: 60, step: '0.1' },
      { key: 'masse_musculaire', label: 'Masse Musculaire', unit: 'kg', min: 10, max: 100, step: '0.1' },
      { key: 'masse_osseuse', label: 'Masse Osseuse', unit: 'kg', min: 1, max: 10, step: '0.1' },
      { key: 'proteine', label: 'Protéine', unit: '%', min: 10, max: 30, step: '0.1' },
      { key: 'taux_metabolique_basal', label: 'Taux Métabolique Basal', unit: 'kcal', min: 800, max: 3000, type: 'number', step: '1' },
      { key: 'age_corporel', label: 'Âge Corporel', unit: 'ans', min: 10, max: 100, type: 'number', step: '1' }
    ])

    // Erreurs de validation
    const validationErrors = ref({})

    // Initialisation des données du formulaire
    const initializeFormData = () => {
      const now = new Date()
      const initialData = {}
      
      // Initialiser les données des métriques
      metrics.value.forEach(metric => {
        initialData[metric.key] = {
          value: '',
          unit: metric.unit,
          status: 'standard'
        }
      })
      
      return {
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().slice(0, 5),
        data: initialData
      }
    }

    // Données du formulaire - initialisées immédiatement
    const formData = reactive(initializeFormData())

    // Validation d'une métrique
    const validateMetric = (key) => {
      const metric = metrics.value.find(m => m.key === key)
      const value = formData.data[key].value
      
      if (value === '' || value === null || value === undefined) {
        delete validationErrors.value[key]
        return
      }

      // Pour les champs avec restrictions min/max, valider les limites
      if (metric.min !== undefined && metric.max !== undefined) {
        if (value < metric.min || value > metric.max) {
          validationErrors.value[key] = `Valeur doit être entre ${metric.min} et ${metric.max}`
        } else {
          delete validationErrors.value[key]
        }
      } else {
        // Pas de restrictions pour ce champ (comme taux_musculaire)
        delete validationErrors.value[key]
      }

      // Calculer le statut basé sur des plages typiques
      formData.data[key].status = calculateStatus(key, value)
    }

    // Calculer le statut d'une valeur
    const calculateStatus = (key, value) => {
      // Plages de référence simplifiées (à ajuster selon les besoins médicaux)
      const ranges = {
        poids: { faible: [0, 50], standard: [50, 90], eleve: [90, 110], trop_eleve: [110, 999] },
        imc: { faible: [0, 18.5], standard: [18.5, 25], eleve: [25, 30], trop_eleve: [30, 999] },
        graisse_corporelle: { faible: [0, 10], standard: [10, 20], eleve: [20, 30], trop_eleve: [30, 999] },
        frequence_cardiaque: { faible: [0, 60], standard: [60, 100], eleve: [100, 120], trop_eleve: [120, 999] },
        graisse_viscerale: { faible: [0, 5], standard: [5, 10], eleve: [10, 15], trop_eleve: [15, 999] },
        eau_corporelle: { faible: [0, 50], standard: [50, 65], eleve: [65, 75], trop_eleve: [75, 999] }
      }

      const range = ranges[key]
      if (!range) return 'standard'

      if (value <= range.faible[1]) return 'faible'
      if (value <= range.standard[1]) return 'standard'
      if (value <= range.eleve[1]) return 'eleve'
      return 'trop-eleve'
    }

    // Classes CSS pour les inputs selon le statut
    const getInputClass = (key) => {
      const status = formData.data[key]?.status
      return {
        'input-faible': status === 'faible',
        'input-standard': status === 'standard',
        'input-excellent': status === 'excellent',
        'input-eleve': status === 'eleve',
        'input-trop-eleve': status === 'trop-eleve'
      }
    }

    // Classes CSS pour les sélecteurs de statut
    const getStatusSelectClass = (key) => {
      const status = formData.data[key]?.status
      return {
        'status-faible': status === 'faible',
        'status-standard': status === 'standard',
        'status-excellent': status === 'excellent',
        'status-eleve': status === 'eleve',
        'status-trop-eleve': status === 'trop-eleve'
      }
    }

    // Validation du formulaire
    const isFormValid = computed(() => {
      if (!formData.date || !formData.time) return false
      if (Object.keys(validationErrors.value).length > 0) return false
      
      // Vérifier qu'au moins une métrique a une valeur
      return metrics.value.some(metric => 
        formData.data[metric.key].value !== '' && 
        formData.data[metric.key].value !== null
      )
    })

    // Soumission du formulaire
    const submitForm = () => {
      if (!isFormValid.value) return

      // Convertir la date au format français
      const dateObj = new Date(formData.date)
      const frenchDate = dateObj.toLocaleDateString('fr-FR')

      const measurement = {
        date: frenchDate,
        time: formData.time,
        data: { ...formData.data }
      }

      emit('measurement-added', measurement)
      resetForm()
    }

    // Réinitialiser le formulaire
    const resetForm = () => {
      const newData = initializeFormData()
      Object.assign(formData, newData)
      validationErrors.value = {}
    }


    return {
      metrics,
      formData,
      validationErrors,
      isFormValid,
      validateMetric,
      getInputClass,
      getStatusSelectClass,
      submitForm,
      resetForm
    }
  }
}
</script>

<style scoped>
.data-form {
  max-width: 100%;
}

.measurement-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #2c3e50;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Classes pour les statuts */
.input-faible {
  border-color: #17a2b8;
  background-color: #cce7ff;
}

.input-standard {
  border-color: #28a745;
  background-color: #d4edda;
}

.input-excellent {
  border-color: #155724;
  background-color: #c3e6cb;
}

.input-eleve {
  border-color: #ffc107;
  background-color: #fff3cd;
}

.input-trop-eleve {
  border-color: #dc3545;
  background-color: #f8d7da;
}

/* Classes pour les sélecteurs de statut */
.status-faible {
  border-color: #17a2b8;
  background-color: #cce7ff;
}

.status-standard {
  border-color: #28a745;
  background-color: #d4edda;
}

.status-excellent {
  border-color: #155724;
  background-color: #c3e6cb;
}

.status-eleve {
  border-color: #ffc107;
  background-color: #fff3cd;
}

.status-trop-eleve {
  border-color: #dc3545;
  background-color: #f8d7da;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.metric-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.metric-group label {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
}

.input-row {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.input-group {
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 200px;
}

.status-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.status-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.status-select {
  padding: 0.5rem;
  border: 2px solid #e1e8ed;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.status-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.input-group .form-input {
  flex: 1;
  padding-right: 3rem;
}

.unit {
  position: absolute;
  right: 0.75rem;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  pointer-events: none;
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e1e8ed;
}

.btn-reset,
.btn-submit {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-reset {
  background: #e1e8ed;
  color: #2c3e50;
}

.btn-reset:hover {
  background: #d1d9e0;
  transform: translateY(-1px);
}

.btn-submit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .input-row {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .input-group {
    min-width: unset;
  }
  
  .status-group {
    min-width: unset;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn-reset,
  .btn-submit {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .metrics-grid {
    gap: 1rem;
  }
  
  .input-group .form-input {
    padding-right: 2.5rem;
  }
  
  .unit {
    right: 0.5rem;
    font-size: 0.8rem;
  }
}
</style>
